// Item library evaluate codes for /roads-sewers/storm-sewers Ponds & Swales
// build-up dev-mode display — mirrors prelimsMapping.ts's description -> code
// lookup pattern. Ponds and Swales share the exact same row descriptions
// (stormBuildUpPonds.ts / stormBuildUpSwales.ts), so one shared lookup covers both.
//
// Three rows embed a live-configured mm value in their description instead of
// fixed text ("Excavate to reduce levels to take up <mm>mm top soil, deposit
// onsite" and the two "Spread and level <mm>mm thick top soil - site won" /
// "- imported" rows) — those are matched by pattern rather than exact text so
// the code still resolves whatever mm value is currently configured (or the
// "xxx" placeholder before it's configured).
//
// "Anchor trench" has no code and is intentionally left out of the map.

export interface StormPondsSwalesEvaluateCodeItem {
  description: string;
  code: string;
}

export const PONDS_SWALES_EVALUATE_CODES: StormPondsSwalesEvaluateCodeItem[] = [
  { description: 'Site strip to remove vegetation', code: 'ALP033' },
  { description: 'Excavate to reduce levels', code: 'ALP037a' },
  { description: 'Filling with site won subsoil', code: 'ALP037' },
  { description: 'Load, cart and bund excavated material', code: 'ALP040' },
  { description: 'Level and compact formation, batter embankments', code: 'ALP035' },
  { description: 'Pond lining or puddle clay', code: 'SCSPON001' },
];

const EXCAVATE_TOPSOIL_PATTERN = /^Excavate to reduce levels to take up (\d+|xxx)mm top soil, deposit onsite$/;
const SPREAD_TOPSOIL_SITEWON_PATTERN = /^Spread and level (\d+|xxx)mm thick top soil - site won$/;
const SPREAD_TOPSOIL_IMPORTED_PATTERN = /^Spread and level (\d+|xxx)mm thick top soil - imported$/;

export function findPondsSwalesEvaluateCode(description: string): string | undefined {
  const fixed = PONDS_SWALES_EVALUATE_CODES.find((i) => i.description === description)?.code;
  if (fixed) return fixed;
  if (EXCAVATE_TOPSOIL_PATTERN.test(description)) return 'ALP013';
  if (SPREAD_TOPSOIL_SITEWON_PATTERN.test(description)) return 'GWALPGW002';
  if (SPREAD_TOPSOIL_IMPORTED_PATTERN.test(description)) return 'GWALPGW035';
  return undefined;
}

// Headwalls — same tab (Ponds & Swales view switcher) as Ponds/Swales above,
// all rows are static text (stormBuildUpHeadwalls.ts) so a plain exact-match
// table is enough, no dynamic patterns needed. "Break out exiting headwall"
// has no code and is intentionally left out of the map.
export const HEADWALL_EVALUATE_CODES: StormPondsSwalesEvaluateCodeItem[] = [
  { description: 'Headwall to suit 225mmØ pipe', code: 'AMPHW001' },
  { description: 'Headwall to suit 300mmØ pipe', code: 'AMPHW002' },
  { description: 'Headwall to suit 375mmØ pipe', code: 'AMPHW003' },
  { description: 'Headwall to suit 450mmØ pipe', code: 'AMPHW004' },
  { description: 'Headwall to suit 525mmØ pipe', code: 'AMPHW010' },
  { description: 'Headwall to suit 600mmØ pipe', code: 'AMPHW011' },
  { description: 'Headwall to suit 750mmØ pipe', code: 'AMPHW012' },
  { description: 'Headwall to suit 900mmØ pipe', code: 'AMPHW013' },
  { description: 'Headwall to suit 1050mmØ pipe', code: 'AMPHW014' },
  { description: 'Headwall to suit 1200mmØ pipe', code: 'AMPHW015' },
  { description: 'Civilise existing ditchcourse immediately adjacent outfall headwall', code: 'GWALPGW037' },
  { description: 'Rip Rap', code: 'AMPHW005' },
];

export function findHeadwallEvaluateCode(description: string): string | undefined {
  return HEADWALL_EVALUATE_CODES.find((i) => i.description === description)?.code;
}
